import { api } from "@/lib/api/client";
import type { components } from "@/types/api";

// ---------------------------------------------------------------------------
// Types from OpenAPI spec
// ---------------------------------------------------------------------------

export type Deployment = components["schemas"]["Deployment"];
export type Package = components["schemas"]["Package"];
export type PackageInfo = components["schemas"]["PackageInfo"];
export type DeploymentMetrics = components["schemas"]["DeploymentMetrics"];
export type DeploymentMetricsHistory = components["schemas"]["DeploymentMetricsHistoryResponse"];
export type HaltHistoryItem = components["schemas"]["HaltHistoryItem"];
export type HaltHistoryResponse = components["schemas"]["HaltHistoryResponse"];

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/**
 * GET /v1/apps/:appName/deployments
 */
export function listDeployments(appName: string): Promise<{ deployments: Deployment[] }> {
  return api.get<{ deployments: Deployment[] }>(
    `/v1/apps/${encodeURIComponent(appName)}/deployments`,
  );
}

export function createDeployment(
  appName: string,
  name: string,
  key?: string,
): Promise<{ deployment: Deployment }> {
  return api.post<{ deployment: Deployment }>(
    `/v1/apps/${encodeURIComponent(appName)}/deployments`,
    key ? { name, key } : { name },
  );
}

/**
 * DELETE /v1/apps/:appName/deployments/:deploymentName
 */
export function deleteDeployment(appName: string, deploymentName: string): Promise<void> {
  return api.delete<void>(
    `/v1/apps/${encodeURIComponent(appName)}/deployments/${encodeURIComponent(deploymentName)}`,
  );
}

/**
 * PATCH /v1/apps/:appName/deployments/:deploymentName
 */
export function renameDeployment(
  appName: string,
  deploymentName: string,
  newName: string,
): Promise<{ deployment: Deployment }> {
  return api.patch<{ deployment: Deployment }>(
    `/v1/apps/${encodeURIComponent(appName)}/deployments/${encodeURIComponent(deploymentName)}`,
    { name: newName },
  );
}

/**
 * GET /v1/apps/:appName/deployments/:deploymentName/history
 */
export function getDeploymentHistory(
  appName: string,
  deploymentName: string,
): Promise<{ history: Package[] }> {
  return api.get<{ history: Package[] }>(
    `/v1/apps/${encodeURIComponent(appName)}/deployments/${encodeURIComponent(deploymentName)}/history`,
  );
}

/**
 * GET /v1/apps/:appName/deployments/:deploymentName/metrics
 */
export function getDeploymentMetrics(
  appName: string,
  deploymentName: string,
): Promise<{ metrics: DeploymentMetrics }> {
  return api.get<{ metrics: DeploymentMetrics }>(
    `/v1/apps/${encodeURIComponent(appName)}/deployments/${encodeURIComponent(deploymentName)}/metrics`,
  );
}

/**
 * GET /v1/apps/:appName/deployments/:deploymentName/metrics/history
 */
export function getDeploymentMetricsHistory(
  appName: string,
  deploymentName: string,
): Promise<DeploymentMetricsHistory> {
  return api.get<DeploymentMetricsHistory>(
    `/v1/apps/${encodeURIComponent(appName)}/deployments/${encodeURIComponent(deploymentName)}/metrics/history`,
  );
}

export function getDeploymentHaltHistory(
  appName: string,
  deploymentName: string,
): Promise<HaltHistoryResponse> {
  return api.get<HaltHistoryResponse>(
    `/v1/apps/${encodeURIComponent(appName)}/deployments/${encodeURIComponent(deploymentName)}/halt-history`,
  );
}

/**
 * PATCH /v1/apps/:appName/deployments/:deploymentName/release
 * Update release metadata. Pass `label` inside packageInfo to target
 * a specific release; otherwise the latest release is updated.
 * Responds 204 without a body when nothing changed.
 */
export function updateRelease(
  appName: string,
  deploymentName: string,
  packageInfo: PackageInfo,
): Promise<{ package: Package } | undefined> {
  return api.patch<{ package: Package } | undefined>(
    `/v1/apps/${encodeURIComponent(appName)}/deployments/${encodeURIComponent(deploymentName)}/release`,
    { packageInfo },
  );
}

/**
 * POST /v1/apps/:appName/deployments/:sourceDeploymentName/promotions
 * Promote the source deployment's release to the destination deployment.
 */
export function promoteRelease(
  appName: string,
  sourceDeploymentName: string,
  destination: string,
  packageInfo: PackageInfo = {},
): Promise<{ package: Package }> {
  return api.post<{ package: Package }>(
    `/v1/apps/${encodeURIComponent(appName)}/deployments/${encodeURIComponent(sourceDeploymentName)}/promotions`,
    { destination, packageInfo },
  );
}

/**
 * POST /v1/apps/:appName/deployments/:deploymentName/rollbacks
 * Roll back to the previous release, or to `targetRelease` when given.
 */
export function rollbackDeployment(
  appName: string,
  deploymentName: string,
  targetRelease?: string,
): Promise<{ package: Package }> {
  return api.post<{ package: Package }>(
    `/v1/apps/${encodeURIComponent(appName)}/deployments/${encodeURIComponent(deploymentName)}/rollbacks`,
    targetRelease ? { targetRelease } : {},
  );
}
